import { Link } from "react-router-dom";
import { Phone, MessageSquare, CalendarCheck, MapPin, Navigation, ExternalLink, FileDown } from "lucide-react";
import Footer from "@/components/Footer";
import { usePageMeta } from "@/seo";
import { trackCall, trackText, trackBook, trackIntake } from "@/analytics";

const MAP_EMBED_URL = "https://www.google.com/maps?q=Dr+Kevin+Greenspan,+4573+US+221+N,+Marion,+NC+28752&output=embed";
const DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=Dr+Kevin+Greenspan,+4573+US+221+N,+Marion,+NC+28752";

export const MARION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Chiropractor",
  "@id": "https://kevingreenspan.com/marion#location",
  name: "Dr. Kevin Greenspan",
  address: {
    "@type": "PostalAddress",
    streetAddress: "4573 US 221 N",
    addressLocality: "Marion",
    addressRegion: "NC",
    postalCode: "28752",
    addressCountry: "US",
  },
  telephone: "+18286913551",
  url: "https://kevingreenspan.com/marion",
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Tuesday", opens: "10:00", closes: "12:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Tuesday", opens: "15:00", closes: "18:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "10:00", closes: "12:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "15:00", closes: "18:00" },
  ],
  paymentAccepted: "Cash, Check",
  priceRange: "$$",
  geo: { "@type": "GeoCoordinates", latitude: 35.73805, longitude: -82.04931 },
  hasMap: "https://www.google.com/maps/place/Dr+Kevin+Greenspan/",
  founder: { "@type": "Person", name: "Kevin Greenspan", jobTitle: "Doctor of Chiropractic" },
};

const HOURS = [
  ["Monday", "Closed"],
  ["Tuesday", "10:00 AM – 12:00 PM, 3:00 – 6:00 PM"],
  ["Wednesday", "Closed"],
  ["Thursday", "10:00 AM – 12:00 PM, 3:00 – 6:00 PM"],
  ["Friday", "Closed"],
  ["Saturday", "Closed"],
  ["Sunday", "Closed"],
];

const SERVICES = [
  ["Full spine chiropractic", "I utilize a variety of adjusting techniques — you pick what works best for you. For those wanting very light force, I offer Activator and ArthroStim adjustments. Prefer a more hands-on approach? I also use Thompson Drop Table techniques and traditional manual adjustments."],
  ["Extremity adjusting", "Not just the spine — I work on shoulders, elbows, wrists, hips, knees, and ankles. I was trained by Dr. Kevin Herron, DC, CCSP at Life College of Chiropractic, a pioneer in extremity adjusting."],
  ["Applied Kinesiology", "A diagnostic system that uses manual muscle testing to evaluate body function. By identifying imbalances in your structural, chemical, and emotional health, we develop a personalized treatment plan."],
  ["Trigenix", "A 15-minute soft-tissue and muscle-reset technique that Dr. Greenspan uses alongside adjusting. It works through the muscles and connective tissue rather than the joints, and can be booked on its own. Dr. Greenspan will tell you at your visit whether it's appropriate for you."],
  ["Class IV K-Laser therapy", "A 10-minute laser session applied to joints and soft tissue. It is done in the treatment room, requires no preparation, and can be booked on its own or added to a visit. Dr. Greenspan will tell you at your visit whether it's appropriate for you."],
];

const Section = ({ id, title, children }) => (
  <section id={id} className="py-10 border-t border-[#E4DCCF]" data-testid={`marion-${id}`}>
    <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A221E] tracking-tight mb-5">{title}</h2>
    {children}
  </section>
);

export default function Marion() {
  usePageMeta("/marion");
  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="marion-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(MARION_SCHEMA) }} />
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E4DCCF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl text-[#1A221E] tracking-tight">Dr. Greenspan</Link>
          <nav className="hidden sm:flex items-center gap-8">
            <Link to="/#about" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">About</Link>
            <Link to="/#services" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Services</Link>
            <Link to="/blog" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Blog</Link>
            <Link to="/black-mountain" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Black Mountain</Link>
          </nav>
          <Link to="/book-online" onClick={trackBook} className="font-body text-sm font-medium text-white bg-[#C87B5D] hover:bg-[#b56a4e] px-5 py-2 rounded-full transition-colors" data-testid="marion-header-book">
            Book Online
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 pt-16 pb-20">
        <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#C87B5D] mb-4">Marion, NC</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1A221E] tracking-tight leading-[1.05] mb-8" data-testid="marion-h1">
          Chiropractic care in Marion, NC
        </h1>

        <section id="where" className="pb-10" data-testid="marion-where">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A221E] tracking-tight mb-5">Where exactly</h2>
          <p className="font-body text-base md:text-lg text-[#5C6A61] leading-relaxed mb-6">
            Dr. Greenspan practices inside Earth Heart Health Foods at 4573 US 221 North, about four miles north of downtown Marion, where US 221 meets Toms Creek Road. Park in the Earth Heart lot and come in the main store entrance; the treatment room is inside the store. Cash and check only.
          </p>
          <div className="rounded-3xl overflow-hidden border border-[#E4DCCF] bg-white" data-testid="marion-map">
            <iframe
              src={MAP_EMBED_URL}
              title="Map to Dr. Kevin Greenspan, 4573 US 221 N, Marion, NC 28752"
              width="100%"
              height="400"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 px-5 py-3 font-body text-sm text-[#3A4F41] hover:bg-[#F7F5F0] transition-colors" data-testid="marion-directions-link">
              <span className="flex items-center gap-2"><Navigation size={14} className="text-[#C87B5D]" /> Get Directions — 4573 US 221 N, Marion, NC 28752</span>
              <ExternalLink size={12} className="text-[#C87B5D]" />
            </a>
          </div>
        </section>

        <Section id="hours" title="Hours">
          <table className="w-full max-w-lg font-body text-sm sm:text-base border-collapse" data-testid="marion-hours-table">
            <thead>
              <tr className="text-left text-[#5C6A61] border-b border-[#E4DCCF]">
                <th className="py-2 pr-6 font-medium">Day</th>
                <th className="py-2 font-medium">Hours</th>
              </tr>
            </thead>
            <tbody>
              {HOURS.map(([day, hours]) => (
                <tr key={day} className="border-b border-[#E4DCCF]/60">
                  <td className="py-2.5 pr-6 text-[#1A221E] font-medium">{day}</td>
                  <td className={`py-2.5 ${hours === "Closed" ? "text-[#5C6A61]" : "text-[#1A221E]"}`}>{hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="font-body text-sm text-[#5C6A61] mt-4">By appointment.</p>
        </Section>

        <Section id="first-visit" title="What a first visit looks like">
          <p className="font-body text-base md:text-lg text-[#5C6A61] leading-relaxed">
            Your first visit runs about 45 minutes. Dr. Greenspan takes a history, examines you, and uses Applied Kinesiology muscle testing alongside the standard orthopedic and chiropractic examination to decide what, if anything, to adjust. If adjusting is appropriate, it usually happens the same visit. You can choose the style of adjustment — very light-force (Activator, ArthroStim), drop-table, or traditional manual. Bring the intake form filled out if you can; it saves ten minutes.
          </p>
          <a href="/intake-form.docx" download="New-Patient-Intake-Form.docx" onClick={trackIntake} className="inline-flex items-center gap-2 mt-5 font-body text-sm text-[#3A4F41] underline underline-offset-4" data-testid="marion-intake-link">
            <FileDown size={16} /> Download the intake form
          </a>
        </Section>

        <Section id="services" title="Services at Marion">
          <div className="space-y-6">
            {SERVICES.map(([name, text]) => (
              <div key={name} data-testid={`marion-service-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-heading text-xl text-[#3A4F41] mb-1">{name}</h3>
                <p className="font-body text-base text-[#5C6A61] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="who" title="Who he sees">
          <p className="font-body text-base md:text-lg text-[#5C6A61] leading-relaxed">
            Infants, children, adults and seniors. From babies to seniors, Dr. Greenspan works with patients at every stage of life. Techniques are adapted to age and comfort.
          </p>
        </Section>

        <Section id="book" title="Book, call or text">
          <div className="flex flex-wrap gap-3" data-testid="marion-actions">
            <Link to="/book-online" onClick={trackBook} className="inline-flex items-center gap-2 rounded-full bg-[#3A4F41] hover:bg-[#2d3e33] text-white font-body text-sm font-medium px-6 py-3 transition-colors" data-testid="marion-book-online">
              <CalendarCheck size={16} /> Book Online
            </Link>
            <a href="tel:+18286913551" onClick={trackCall("marion")} className="inline-flex items-center gap-2 rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body text-sm font-medium px-6 py-3 transition-colors" data-testid="marion-call">
              <Phone size={16} /> Call (828) 691-3551
            </a>
            <a href="sms:+18286913551" onClick={trackText("marion")} className="inline-flex items-center gap-2 rounded-full border border-[#3A4F41] text-[#3A4F41] hover:bg-[#3A4F41]/5 font-body text-sm font-medium px-6 py-3 transition-colors" data-testid="marion-text">
              <MessageSquare size={16} /> Text (828) 691-3551
            </a>
          </div>
          <p className="font-body text-sm text-[#5C6A61] mt-4 flex items-center gap-2"><MapPin size={14} className="text-[#C87B5D]" /> Inside Earth Heart Health Foods · 4573 US 221 N, Marion, NC 28752</p>
        </Section>

        <p className="font-body text-sm text-[#5C6A61] pt-8 border-t border-[#E4DCCF]" data-testid="marion-black-mountain-note">
          Dr. Greenspan also sees established patients in{" "}
          <Link to="/black-mountain" className="text-[#3A4F41] underline underline-offset-4">Black Mountain</Link>
          {" "}on alternate Saturdays, by phone booking.
        </p>
      </main>

      <Footer />
    </div>
  );
}
